<template>
  <div class="flex flex-col h-full gap-4">
    <div class="flex flex-col gap-1">
      <label class="text-sm text-gray-500">שם השדה</label>
      <InputText v-model="label" placeholder="למשל: עוצמת מכונת לייזר" class="w-full" />
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-sm text-gray-500">סוג</label>
      <div class="flex gap-2">
        <div
          v-for="option in typeOptions"
          :key="option.value"
          class="flex-1 text-center py-2.5 rounded-lg border cursor-pointer text-sm select-none"
          :class="type === option.value ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium' : 'border-gray-300 text-gray-700'"
          @click="type = option.value"
        >
          {{ option.label }}
        </div>
      </div>
    </div>

    <div v-if="type === CustomFieldTypeEnum.Number" class="flex flex-col gap-1">
      <label class="text-sm text-gray-500">יחידת מידה (רשות)</label>
      <InputText v-model="unit" placeholder="למשל: J/cm²" class="w-full" />
    </div>

    <div v-if="type === CustomFieldTypeEnum.Select" class="flex flex-col gap-1">
      <label class="text-sm text-gray-500">אפשרויות (מופרדות בפסיק)</label>
      <InputText v-model="optionsText" placeholder="רגיל, שמן, יבש, מעורב" class="w-full" />
    </div>

    <div class="flex justify-between items-center py-2 cursor-pointer select-none" @click="trackHistory = !trackHistory">
      <div class="flex flex-col gap-0.5">
        <span class="font-medium">תיעוד היסטוריית שינויים</span>
        <span class="text-xs text-gray-500">שמרו כל ערך קודם עם תאריך</span>
      </div>
      <ToggleSwitch v-model="trackHistory" @click.stop />
    </div>

    <drawer-footer
      class="mt-auto"
      approve-label="יצירת שדה"
      :approveDisable="!isValid || loading"
      :approveLoading="loading"
      @approve-click="save"
      @cancel-click="$emit('close')"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { CustomFieldTypeEnum, ICustomFieldDefinition } from '@torim-app/types';
import ToggleSwitch from 'primevue/toggleswitch';

const props = defineProps<{
  onSave: (definition: Omit<ICustomFieldDefinition, 'id'>) => Promise<void>;
}>();

const emit = defineEmits(['close']);

const typeOptions: Array<{ label: string; value: CustomFieldTypeEnum }> = [
  { label: 'מספר', value: CustomFieldTypeEnum.Number },
  { label: 'טקסט', value: CustomFieldTypeEnum.Text },
  { label: 'בחירה מרשימה', value: CustomFieldTypeEnum.Select }
];

const label = ref('');
const type = ref<CustomFieldTypeEnum>(CustomFieldTypeEnum.Number);
const unit = ref('');
const optionsText = ref('');
const trackHistory = ref(true);
const loading = ref(false);

const parsedOptions = computed(() => optionsText.value.split(',').map(option => option.trim()).filter(Boolean));
const isValid = computed(() =>
  !!label.value.trim() && (type.value !== CustomFieldTypeEnum.Select || parsedOptions.value.length >= 2)
);

const save = async () => {
  if (!isValid.value) return;

  const definition: Omit<ICustomFieldDefinition, 'id'> = {
    label: label.value.trim(),
    type: type.value,
    trackHistory: trackHistory.value
  };
  if (type.value === CustomFieldTypeEnum.Number && unit.value.trim())
    definition.unit = unit.value.trim();
  if (type.value === CustomFieldTypeEnum.Select)
    definition.options = parsedOptions.value;

  loading.value = true;
  try {
    await props.onSave(definition);
    emit('close');
  } finally {
    loading.value = false;
  }
};
</script>
